import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ImpressaoEtiquetaService } from '../../services/apis/etiqueta/impressao-etiqueta.service';
import { EtiquetaImprimirRequest } from '../../services/requests/etiqueta/etiqueta-imprimir.request';
import { AlertService, AlertType } from '../../services/alert.service';

export interface ImpressaoEtiquetaPreviewDialogData {
  htmlContent: string;
  request: EtiquetaImprimirRequest;
}

@Component({
  selector: 'app-impressao-etiqueta-preview-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './impressao-etiqueta-preview-dialog.component.html',
  styleUrls: ['./impressao-etiqueta-preview-dialog.component.css'],
})
export class ImpressaoEtiquetaPreviewDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('previewFrame') previewFrame!: ElementRef<HTMLIFrameElement>;
  @ViewChild('contentBody') contentBody!: ElementRef<HTMLDivElement>;

  public safeHtml: SafeHtml = '';
  public isPrinting = false;
  public paginaAtual = 1;
  public totalPaginas = 1;
  public paginaInformada = '1';

  private pageOffsets: number[] = [];
  private scrollListener: (() => void) | null = null;

  constructor(
    private dialogRef: MatDialogRef<ImpressaoEtiquetaPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoEtiquetaPreviewDialogData,
    private sanitizer: DomSanitizer,
    private impressaoEtiquetaService: ImpressaoEtiquetaService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(this.data.htmlContent);
  }

  ngAfterViewInit(): void {
    const container = this.contentBody?.nativeElement;
    if (!container) return;

    this.scrollListener = (): void => this.atualizarPaginaAtualPorScroll();
    container.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  ngOnDestroy(): void {
    if (this.scrollListener && this.contentBody?.nativeElement) {
      this.contentBody.nativeElement.removeEventListener('scroll', this.scrollListener);
    }
  }

  public onIframeLoad(): void {
    const iframe = this.previewFrame?.nativeElement;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc?.body) {
      iframe.style.height = doc.body.scrollHeight + 'px';
    }

    this.mapearPaginas();
    this.atualizarPaginaAtualPorScroll();
  }

  public onPaginaInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmarPaginaInformada();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.paginaInformada = this.paginaAtual.toString();
    }
  }

  public onPaginaInputBlur(): void {
    this.confirmarPaginaInformada();
  }

  public onPaginaInputFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  public imprimir(): void {
    this.isPrinting = true;

    this.impressaoEtiquetaService.gerarPdf(this.data.request).subscribe({
      next: (blob: Blob) => {
        this.isPrinting = false;
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      error: (err: any) => {
        this.isPrinting = false;
        this.alertService.show('Erro', 'Erro ao gerar PDF de impressão.', AlertType.error);
        console.error(err);
      },
    });
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  private mapearPaginas(): void {
    const iframe = this.previewFrame?.nativeElement;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc?.body) return;

    const pages = Array.from(doc.querySelectorAll('.page')) as HTMLElement[];
    if (pages.length > 0) {
      const bodyRect = doc.body.getBoundingClientRect();
      this.pageOffsets = pages.map(page => {
        const rect = page.getBoundingClientRect();
        return Math.max(rect.top - bodyRect.top, 0);
      });
    } else {
      this.pageOffsets = [0];
    }

    this.totalPaginas = Math.max(this.pageOffsets.length, 1);
    if (this.paginaAtual > this.totalPaginas) {
      this.paginaAtual = this.totalPaginas;
    }
    this.paginaInformada = this.paginaAtual.toString();
  }

  private atualizarPaginaAtualPorScroll(): void {
    const container = this.contentBody?.nativeElement;
    const iframe = this.previewFrame?.nativeElement;

    if (!container || !iframe || this.pageOffsets.length === 0) {
      return;
    }

    const offsetTopIframeNoContainer = iframe.offsetTop;
    const scrollDentroDoIframe = Math.max(container.scrollTop - offsetTopIframeNoContainer, 0);
    const pontoReferencia = scrollDentroDoIframe + container.clientHeight / 2;

    let paginaCalculada = 1;
    for (let i = 0; i < this.pageOffsets.length; i++) {
      if (pontoReferencia >= this.pageOffsets[i]) {
        paginaCalculada = i + 1;
      } else {
        break;
      }
    }

    if (paginaCalculada !== this.paginaAtual) {
      this.paginaAtual = paginaCalculada;
      this.paginaInformada = this.paginaAtual.toString();
    }
  }

  private confirmarPaginaInformada(): void {
    const pagina = Number(this.paginaInformada);
    const paginaValida = Number.isInteger(pagina) && pagina >= 1 && pagina <= this.totalPaginas;

    if (!paginaValida) {
      this.paginaInformada = this.paginaAtual.toString();
      return;
    }

    if (pagina === this.paginaAtual) {
      this.paginaInformada = this.paginaAtual.toString();
      return;
    }

    this.irParaPagina(pagina);
  }

  private irParaPagina(pagina: number): void {
    const container = this.contentBody?.nativeElement;
    const iframe = this.previewFrame?.nativeElement;

    if (!container || !iframe || pagina < 1 || pagina > this.totalPaginas) {
      this.paginaInformada = this.paginaAtual.toString();
      return;
    }

    const topDestino = this.obterTopDestinoPagina(pagina);
    if (topDestino == null) {
      this.paginaInformada = this.paginaAtual.toString();
      return;
    }

    const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
    container.scrollTo({ top: Math.min(topDestino, maxScrollTop), behavior: 'smooth' });

    this.paginaAtual = pagina;
    this.paginaInformada = pagina.toString();
  }

  private obterTopDestinoPagina(pagina: number): number | null {
    const container = this.contentBody?.nativeElement;
    const iframe = this.previewFrame?.nativeElement;
    if (!container || !iframe) return null;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return null;

    const pages = Array.from(doc.querySelectorAll('.page')) as HTMLElement[];
    const page = pages[pagina - 1];
    if (!page) return null;

    // Usa getBoundingClientRect para calcular posição independente de offsetParent.
    const containerRect = container.getBoundingClientRect();
    const iframeRect = iframe.getBoundingClientRect();
    const pageRect = page.getBoundingClientRect();

    const distanciaAoContainer = iframeRect.top - containerRect.top + pageRect.top;

    return Math.max(container.scrollTop + distanciaAoContainer, 0);
  }
}
